import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router';
import { ConfigProvider, App as AntdApp } from 'antd';
import { AuthProvider, useAuth } from './providers/AuthProvider';
import { RoleProvider, useRole } from './providers/RoleProvider';
import { AppLayout } from './layouts/AppLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { Loader } from './components/UI/Loader';
import { Login } from './pages/Authentication/Login';
import { Register } from './pages/Authentication/Register';
import { ForgotPassword } from './pages/Authentication/ForgotPassword';
import { ResetPassword } from './pages/Authentication/ResetPassword';
import { ResendSignUpToken } from './pages/Authentication/ResendSignUpToken';
import { VerifySignUp } from './pages/Authentication/VerifySignUp';
import { Dashboard } from './pages/Dashboard';
import { PageNotFound } from './pages/PageNotFound';
import { PrivateRoute } from './components/PrivateRoute';
import { ProfileViewPage } from './pages/User/Profile/View';
import { MyPlansList } from './pages/MyPlans/List';
import UserLayout from './pages/User/Layout';
import { PlanView } from './pages/Plans/View';
import MapGuestList from './pages/Map/MapGuestList';
import SiteVisitPage from './pages/Site-Visit/Create';
import { ToolListPage } from './pages/Tools/List';
import SiteVisitList from './pages/Site-Visit/List';
import PageSiteVisitView from './pages/Site-Visit/View';
import SiteVisitEditPage from './pages/Site-Visit/Edit';
import ProposalViewPage from './pages/Proposal/View';

// --- Admin (lazy, separate chunk) ---
const AdminRoutes = lazy(() => import(/* webpackChunkName: "admin" */ './pages/Admin/Routes'));
const AdminProposalCreate = lazy(
  () => import(/* webpackChunkName: "admin" */ './pages/Admin/Proposals/Create')
);

// --- Contractor (lazy, separate chunk) ---
const ContractorRoutes = lazy(
  () => import(/* webpackChunkName: "contractor" */ './pages/Contractor/Routes')
);

// --- Project (lazy, separate chunk) ---
const ProjectRoutes = lazy(
  () => import(/* webpackChunkName: "project" */ './modules/project/ProjectRoutes')
);

const RootRedirect: React.FC = () => {
  const { loggedIn, user } = useAuth();
  const { activeRole, availableRoles } = useRole();

  if (!loggedIn) {
    return <Navigate to="/login" replace />;
  }

  const resolvedRole =
    activeRole ||
    (user?.roles?.includes('ADMIN')
      ? 'ADMIN'
      : user?.roles?.includes('CONTRACTOR')
        ? 'CONTRACTOR'
        : availableRoles[0]);

  if (resolvedRole === 'CONTRACTOR') {
    return <Navigate to="/contractor/proposals" replace />;
  }

  if (resolvedRole === 'ADMIN') {
    return <Navigate to="/admin" replace />;
  }

  return <Navigate to="/project" replace />;
};

const App: React.FC = () => {
  return (
    <ConfigProvider
      theme={{
        token: {},
        components: {
          Layout: {
            headerBg: '#ffffff',
            headerColor: '#000000',
          },
          Card: {},
          Button: {
            // colorPrimary: '#ee8865',
            // colorPrimaryHover: '#f2a183',
            // defaultHoverColor: '#f2a183',
            // defaultHoverBorderColor: '#f2a183',
            // colorPrimaryActive: '#f2a183',
            // colorPrimaryText: '#ffffff',
            // defaultActiveBorderColor: '#ee8865',
            // defaultActiveColor: '#f2a183',
            // groupBorderColor: '#f2a183',
            // textTextActiveColor: '#ffffff',
            // textTextColor: '#ffffff',
            // textTextHoverColor: '#ffffff',
          },
          Carousel: {
            dotWidth: 8,
            dotHeight: 8,
            dotActiveWidth: 12,
          },
        },
      }}>
      <AntdApp>
        <AuthProvider>
          <RoleProvider>
            <Router>
              <Routes>
                {/* Auth pages — split-screen layout, no GuestLayout */}
                <Route element={<AuthLayout />}>
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                </Route>

                {/* Everything else — AppLayout resolves Guest/Logged */}
                <Route element={<AppLayout />}>
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password/:token" element={<ResetPassword />} />
                  <Route path="/resend-signup-token" element={<ResendSignUpToken />} />
                  <Route path="/verify-sign-up/:token" element={<VerifySignUp />} />

                  <Route path="/user" element={<UserLayout />}>
                    <Route index element={<ProfileViewPage />} />
                    <Route path="profile" element={<ProfileViewPage />} />
                  </Route>

                  <Route path="/plans" element={<MapGuestList />} />
                  <Route path="/proposal/:id" element={<ProposalViewPage />} />

                  <Route
                    path="/admin/proposal/new"
                    element={
                      <Suspense fallback={<Loader />}>
                        <PrivateRoute permission={['ADMIN']}>
                          <AdminProposalCreate />
                        </PrivateRoute>
                      </Suspense>
                    }
                  />
                  <Route
                    path="/admin/*"
                    element={
                      <Suspense fallback={<Loader />}>
                        <AdminRoutes />
                      </Suspense>
                    }
                  />

                  <Route
                    path="/project/*"
                    element={
                      <Suspense fallback={<Loader />}>
                        <ProjectRoutes />
                      </Suspense>
                    }
                  />

                  <Route
                    path="/tools"
                    element={
                      <PrivateRoute>
                        <ToolListPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/site-visit/new"
                    element={
                      <PrivateRoute>
                        <SiteVisitPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/site-visit/list"
                    element={
                      <PrivateRoute>
                        <SiteVisitList />
                      </PrivateRoute>
                    }
                  />
                  <Route path="/site-visit/:id" element={<PageSiteVisitView />} />
                  <Route
                    path="/site-visit/edit/:id"
                    element={
                      <PrivateRoute>
                        <SiteVisitEditPage />
                      </PrivateRoute>
                    }
                  />
                  <Route path="/plan/:id/:mapId?" element={<PlanView />} />

                  <Route
                    path="/my-plans"
                    element={
                      <PrivateRoute>
                        <MyPlansList />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/map"
                    element={
                      <PrivateRoute>
                        <Dashboard />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/contractor/*"
                    element={
                      <Suspense fallback={<Loader />}>
                        <ContractorRoutes />
                      </Suspense>
                    }
                  />
                  <Route path="/" element={<RootRedirect />} />
                  <Route path="*" element={<PageNotFound />} />
                </Route>
              </Routes>
            </Router>
          </RoleProvider>
        </AuthProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;
